/**
 * Workspace's tabs view (glossary: Workspace views): the default left side,
 * one chats list with the Chats page's chip bar across the whole Scope. The
 * canvas (the widget board) is the other view, picked under Layout.
 */
import type { TFunction } from 'i18next';
import type { RowBadge } from '../chats/TabRow';
import type { CardItem } from './widgets/ChatCollection';

export type WorkspaceView = 'tabs' | 'canvas';
export type WorkspaceTab = 'activity' | 'suggestions' | 'active' | 'replied' | 'pinned';

/** The bar, in order (fixed: no editor, no All). */
export const WORKSPACE_TABS: WorkspaceTab[] = ['activity', 'suggestions', 'active', 'replied', 'pinned'];

/** Phones get the tabs view only; the canvas needs a desktop. */
export const PHONE_QUERY = '(max-width: 767px)';

/** The view to draw: the person's choice, but always tabs on a phone. */
export function viewOf(setting: unknown, phone: boolean): WorkspaceView {
  return !phone && setting === 'canvas' ? 'canvas' : 'tabs';
}

const LAST_TAB_KEY = 'om_ws_last_tab';

/** The tab last used in this browser; Activity the first time. */
export function readLastTab(): WorkspaceTab {
  try {
    const raw = localStorage.getItem(LAST_TAB_KEY);
    if (raw && (WORKSPACE_TABS as string[]).includes(raw)) return raw as WorkspaceTab;
  } catch {
    /* storage unavailable */
  }
  return 'activity';
}

export function saveLastTab(tab: WorkspaceTab) {
  try {
    localStorage.setItem(LAST_TAB_KEY, tab);
  } catch {
    /* storage unavailable */
  }
}

/** The Chats page's list width: where the tabs view starts. */
export const TABS_DEFAULT_WIDTH = 400;
const TABS_MIN = 300;
const CHAT_MIN = 620;

/** Width of the tabs list: the person's own (dragged) width or the default, never squeezing the chat. */
export function tabsZoneWidth(saved: unknown, rowWidth: number): number {
  const w = typeof saved === 'number' && Number.isFinite(saved) ? saved : TABS_DEFAULT_WIDTH;
  const max = Math.max(TABS_MIN, rowWidth - CHAT_MIN);
  return Math.round(Math.min(Math.max(TABS_MIN, w), max));
}

/** A card's chip on a tab row: the Activity kind, Spent, Fan replied / Waiting, or New on a pin. */
export function badgeFor(tab: WorkspaceTab, card: CardItem, t: TFunction): RowBadge | undefined {
  switch (tab) {
    case 'activity':
      return card.kind ? { icon: card.kind.icon, label: card.kind.label, amount: card.kind.amount, hue: card.kind.hue } : undefined;
    case 'suggestions':
      return card.spent ? { icon: 'dollar-sign', label: t('tabsView.badge.spent'), amount: card.spent, tone: 'accent' } : undefined;
    case 'active':
    case 'replied':
      return card.flag ? { icon: 'message-circle', label: t('tabsView.badge.fanReplied'), tone: 'accent' } : { icon: 'clock', label: t('tabsView.badge.waiting'), tone: 'muted' };
    case 'pinned':
      return card.flag ? { icon: 'message-circle', label: card.flag, tone: 'accent' } : undefined;
  }
}
